import { LcAddress } from "@/domains/core"

import type { IVoteOptimizationGauge } from "./vote-optimization-strategy"

export const calculateOptimalDistribution = (
  gauges: Array<IVoteOptimizationGauge>,
  veEqualAmount: number,
  isDegenMode: boolean = false,
) => {
  // Calculate projected earnings with vote dilution for each gauge
  const gaugesWithProjectedIncome = gauges.map((gauge) => {
    const currentVotes = Math.max(100, Number(gauge.votes))
    const projectedVotes = currentVotes + veEqualAmount * 0.2
    const projectedIncomePerVote = Number(gauge.totalBribeValueUsd) / projectedVotes

    // this breaks MobX reactivity but as were inside a one time computation that's acceptable
    // we should still look for a more natural solution though imo
    return {
      ...gauge,
      projectedIncomePerVote,
      projectedEarnings: projectedIncomePerVote * (veEqualAmount * 0.2),
    }
  })

  const topGauges = gaugesWithProjectedIncome.sort((a, b) => b.projectedEarnings - a.projectedEarnings).slice(0, 10)

  if (!isDegenMode) {
    // Try both 5-way and 10-way splits in safe mode
    const fiveWayAllocation = topGauges.slice(0, 5).map((gauge) => ({
      address: gauge.revenueSourceAddress,
      percentage: 20,
    }))

    const tenWayAllocation = topGauges.map((gauge) => ({
      address: gauge.revenueSourceAddress,
      percentage: 10,
    }))

    const fiveWayEarnings = calculateEarningsForAllocation(fiveWayAllocation, topGauges, veEqualAmount)
    const tenWayEarnings = calculateEarningsForAllocation(tenWayAllocation, topGauges, veEqualAmount)

    return tenWayEarnings > fiveWayEarnings ? tenWayAllocation : fiveWayAllocation
  }

  // Degen mode: Try concentrated positions
  // If only one gauge, allocate 100% to it
  if (topGauges.length === 1) {
    return [{ address: topGauges[0]!.revenueSourceAddress, percentage: 100 }]
  }

  // If no gauges, return empty array
  if (topGauges.length === 0) {
    return []
  }

  // Try concentrated positions with top 2 gauges
  let bestAllocation: Array<{ address: LcAddress; percentage: number }> | null = null
  let bestEarnings = 0

  for (let i = 60; i <= 90; i += 5) {
    const allocation = [
      { address: topGauges[0]!.revenueSourceAddress, percentage: i },
      { address: topGauges[1]!.revenueSourceAddress, percentage: 100 - i },
    ]

    const earnings = calculateEarningsForAllocation(allocation, topGauges, veEqualAmount)
    if (earnings > bestEarnings) {
      bestEarnings = earnings
      bestAllocation = allocation
    }
  }

  // Return best allocation, or fallback to 100% on first gauge if somehow no allocation was found
  return bestAllocation ?? [{ address: topGauges[0]!.revenueSourceAddress, percentage: 100 }]
}

function calculateEarningsForAllocation(
  allocation: { address: LcAddress; percentage: number }[],
  gauges: Array<IVoteOptimizationGauge & { projectedIncomePerVote?: number }>,
  veEqualAmount: number,
) {
  return allocation.reduce((total, { address, percentage }) => {
    const gauge = gauges.find((g) => g.revenueSourceAddress.equals(address))
    if (!gauge) return total
    const voteAmount = veEqualAmount * (percentage / 100)
    const newTotalVotes = Number(gauge.votes) + voteAmount
    const projectedIncomePerVote = Number(gauge.totalBribeValueUsd) / newTotalVotes
    return total + projectedIncomePerVote * voteAmount
  }, 0)
}

// --- BEAST MODE LOGIC ---
// This is the new 'beast' mode logic that calculates optimal vote distribution
// based on bribes per vote and dilution
export const calculateBeastDistribution = (
  gauges: Array<IVoteOptimizationGauge>,
  veEqualAmount: number,
  isBeastMode: boolean = false,
) => {
  if (!isBeastMode) {
    // fallback to safe mode logic if not beast
    const fiveWayAllocation = gauges.slice(0, 5).map((gauge) => ({
      address: gauge.revenueSourceAddress,
      percentage: 20,
    }))
    const tenWayAllocation = gauges.map((gauge) => ({
      address: gauge.revenueSourceAddress,
      percentage: 10,
    }))
    const fiveWayEarnings = calculateEarningsForAllocation(fiveWayAllocation, gauges, veEqualAmount)
    const tenWayEarnings = calculateEarningsForAllocation(tenWayAllocation, gauges, veEqualAmount)
    return tenWayEarnings > fiveWayEarnings ? tenWayAllocation : fiveWayAllocation
  }

  // Sanitize votes - ensure minimum of 1 vote per gauge
  const gaugeVotes = gauges.map((gauge) => Math.max(1, Number(gauge.votes)))
  const currentTotalVotes = gaugeVotes.reduce((a, c) => a + c, 0)
  const totalVotes = currentTotalVotes + veEqualAmount

  // Calculate bribes and fees
  const currentBribes = gauges.map((gauge) => Number(gauge.totalBribeValueUsd))
  const totalBribes = currentBribes.reduce((a, c) => a + c, 0)

  // Calculate average bribes per vote
  const averageBribePerVote = totalBribes / totalVotes
  const gaugesBribePerVote = currentBribes.map((bribe, i) => bribe / (gaugeVotes[i] ?? 1))
  const gaugesBribePerVoteDiff = gaugesBribePerVote.map((bpv) => bpv - averageBribePerVote)

  // Filter out over-diluted gauges (those with below average bribes per vote)
  const chosenBribes = gaugesBribePerVoteDiff.map((diff, i) => (diff > 0 ? (currentBribes[i] ?? 0) : 0))
  const chosenBribesTotal = chosenBribes.reduce((a, c) => (a ?? 0) + (c ?? 0), 0)
  const chosenVotes = gaugesBribePerVoteDiff.map((diff, i) => (diff > 0 ? (gaugeVotes[i] ?? 0) : 0))
  const chosenVotesTotal = chosenVotes.reduce((a, c) => (a ?? 0) + (c ?? 0), 0)

  // Calculate target bribes per vote and required votes
  const targetBribePerVote = (chosenBribesTotal ?? 0) / ((chosenVotesTotal ?? 0) + veEqualAmount)
  const targetVotes = chosenVotes.map((_, i) => (chosenBribes[i] ?? 0) / targetBribePerVote)
  let neededVotes = targetVotes.map((target, i) => Math.max(0, target - (chosenVotes[i] ?? 0)))

  // Normalize so that the sum of neededVotes is at most 100% of voting power
  const totalNeededVotes = neededVotes.reduce((a, c) => (a ?? 0) + (c ?? 0), 0)
  let allocation: { address: LcAddress; percentage: number }[] = []
  if (totalNeededVotes > 0) {
    allocation = gauges.map((gauge, i) => ({
      address: gauge.revenueSourceAddress,
      percentage: ((neededVotes[i] ?? 0) / totalNeededVotes) * 100,
    }))
    // Step 1: Set sub-1% allocations to 0
    allocation = allocation.map((a) => ({ ...a, percentage: a.percentage < 1 ? 0 : a.percentage }))
    // Step 2: Filter out zero allocations
    let nonZero = allocation.filter((a) => a.percentage > 0)
    // Step 3: Renormalize if any allocations remain
    if (nonZero.length > 0) {
      const sum = nonZero.reduce((acc, a) => acc + a.percentage, 0)
      nonZero = nonZero.map((a) => ({ ...a, percentage: (a.percentage / sum) * 100 }))
      // Step 4: Fix floating point error to ensure sum is exactly 100%
      const newSum = nonZero.reduce((acc, a) => acc + a.percentage, 0)
      const diff = 100 - newSum
      if (Math.abs(diff) > 1e-8) {
        const maxIdx = nonZero.reduce<number>((maxI, a, i, arr) => (a.percentage > arr[maxI]!.percentage ? i : maxI), 0)
        if (nonZero[maxIdx]) {
          nonZero[maxIdx]!.percentage += diff
        }
      }
      allocation = nonZero
    } else {
      // Step 5: If all allocations are below 1%, assign 100% to the gauge with the highest original allocation
      if (allocation.length === 0) {
        return []
      }
      const maxIdx = allocation.reduce<number>(
        (maxI, a, i, arr) => (a.percentage > arr[maxI]!.percentage ? i : maxI),
        0,
      )
      const maxAllocation = allocation[maxIdx]
      if (maxAllocation) {
        allocation = [{ address: maxAllocation.address, percentage: 100 }]
      }
    }
  }

  // Step 1: Round down all percentages and track remainders
  let rounded = allocation.map((a) => ({
    ...a,
    floored: Math.floor(a.percentage),
    remainder: a.percentage - Math.floor(a.percentage),
  }))

  let sum = rounded.reduce((acc, a) => acc + a.floored, 0)
  let toDistribute = 100 - sum

  // Step 2: Distribute the remaining votes to the largest remainders
  rounded.sort((a, b) => b.remainder - a.remainder)
  for (let i = 0; i < toDistribute; i++) {
    const item = rounded[i]
    if (item) {
      item.floored += 1
    }
  }

  // Step 3: Final allocation, only include those with at least 1%
  const final = rounded.map((a) => ({ address: a.address, percentage: a.floored })).filter((a) => a.percentage >= 1)

  return final
}
